import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import {
  RobotOutlined, MedicineBoxOutlined,
  ExperimentOutlined, FileTextOutlined,
  TeamOutlined, HeartOutlined, SafetyCertificateOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const COLORS = {
  primary: '#215fc6',
  primaryLight: '#3b82e3',
  primaryBg: '#e2eefd',
  pageBg: '#f0f5ff',
  textMain: '#2c3e50',
  textSub: '#6b7d8e',
  textLight: '#94a3b8',
  border: '#e2e8f0',
  gradient: 'linear-gradient(135deg, #1a4fa0 0%, #215fc6 40%, #3b82e3 100%)',
};

const injectStyles = () => {
  if (document.getElementById('xm-styles')) return;
  const style = document.createElement('style');
  style.id = 'xm-styles';
  style.textContent = `
    @keyframes xm-float{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(-12px) rotate(3deg)}}
    @keyframes xm-float2{0%,100%{transform:translateY(0) rotate(0)}50%{transform:translateY(-8px) rotate(-2deg)}}
    @keyframes xm-fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
    @keyframes xm-pulse{0%{transform:scale(1);opacity:.15}50%{transform:scale(1.08);opacity:.25}100%{transform:scale(1);opacity:.15}}
    @keyframes xm-gradientShift{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
    @media(max-width:600px){.nav-desktop{display:none!important}.nav-mobile{display:flex!important}}
    .hero-title{font-size:32px}.hero-subtitle{font-size:20px}
    @media(max-width:768px){.hero-title{font-size:24px}.hero-subtitle{font-size:16px}}
    @media(max-width:480px){.hero-title{font-size:20px}.hero-subtitle{font-size:14px}}

    /* H5移动端适配 */
    @media(max-width:768px){
      .h5-hero{padding:32px 16px 44px!important}
      .h5-func{gap:8px!important;padding:18px 16px!important}
      .h5-func-item{gap:6px!important;padding:10px 4px!important}
      .h5-func-icon{width:44px!important;height:44px!important;border-radius:50%!important;font-size:22px!important}
      .h5-hl{grid-template-columns:repeat(2,1fr)!important;gap:12px!important}
      .h5-footer-inner{flex-direction:column!important;align-items:center!important;gap:6px!important}
      .h5-footer-copy{text-align:center!important;width:100%!important}
      .h5-root{height:auto!important;min-height:100vh!important;overflow:visible!important}
      .h5-pc{display:none!important}
      .h5-mobile{display:block!important}
    }
  `;
  document.head.appendChild(style);
};

const FloatingCircles = () => (
  <>
    <div style={{position:'absolute',width:280,height:280,borderRadius:'50%',background:'rgba(255,255,255,.06)',top:-90,right:-70,animation:'xm-float 6s ease-in-out infinite'}}/>
    <div style={{position:'absolute',width:180,height:180,borderRadius:'50%',background:'rgba(255,255,255,.04)',bottom:-50,left:-40,animation:'xm-float2 8s ease-in-out infinite'}}/>
    <div style={{position:'absolute',width:100,height:100,borderRadius:'50%',background:'rgba(255,255,255,.05)',top:'30%',left:'10%',animation:'xm-pulse 4s ease-in-out infinite'}}/>
    <div style={{position:'absolute',width:60,height:60,borderRadius:'50%',background:'rgba(255,255,255,.04)',bottom:'20%',right:'15%',animation:'xm-float 5s ease-in-out infinite 1s'}}/>
  </>
);

const BrandFooter = ({ siteCfg }) => (
  <footer style={{background:'#0a1628',color:'rgba(255,255,255,.4)',padding:'14px 0',fontSize:12,textAlign:'center',flexShrink:0}}>
    <div className="h5-footer-inner r-public-col" style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        <MedicineBoxOutlined style={{fontSize:14,color:COLORS.primaryLight}}/>
        <span style={{color:'rgba(255,255,255,.6)',fontSize:13,fontWeight:600}}>{siteCfg?.footer_brand_name||'小铭健康'}</span>
        <span style={{color:'rgba(255,255,255,.3)',fontSize:11,marginLeft:4}}>{siteCfg?.footer_brand_sub||'AI智能辅诊开方系统'}</span>
      </div>
      <div className="h5-pc" style={{fontSize:11,color:'rgba(255,255,255,.3)'}}>{siteCfg?.footer_text||'© 2026 小铭健康科技云平台 · 仅供医疗专业人员参考使用'}</div>
      <div className="h5-mobile" style={{fontSize:11,color:'rgba(255,255,255,.3)',display:'none',textAlign:'center',width:'100%'}}>© 2026 小铭健康科技云平台<br/>AI智能辅诊开方系统 · 仅供医疗专业人员参考使用</div>
    </div>
  </footer>
);

const PublicHomePage = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [siteCfg, setSiteCfg] = useState(null);
  // 后台可增删的顶部导航列表：header_navs JSON（兜底旧 nav_1/2 键）；指向当前页的按钮自动隐藏
  const headerNavs = (() => {
    let list = [];
    try { list = siteCfg?.header_navs ? JSON.parse(siteCfg.header_navs) : []; } catch {}
    if (!Array.isArray(list) || !list.length) {
      list = [siteCfg?.header_nav_1_label && { label: siteCfg.header_nav_1_label, link: siteCfg.header_nav_1_link || '/intro' },
              siteCfg?.header_nav_2_label && { label: siteCfg.header_nav_2_label, link: siteCfg.header_nav_2_link || '/clinic' }].filter(Boolean);
    }
    return list.filter(n => n && n.label && n.link && n.link !== window.location.pathname);
  })();
  const goLink = (link) => { if (/^https?:/.test(link)) window.open(link, '_blank'); else navigate(link); };
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    injectStyles();
    fetch('/api/site-settings').then(r=>r.json()).then(setSiteCfg).catch(()=>{});
    const onScroll=()=>setScrolled(window.scrollY>10);
    window.addEventListener("scroll",onScroll,{passive:true});
    return()=>window.removeEventListener("scroll",onScroll);
  }, []);

  /* 首页输入框：带症状进 AI问诊页自动发送 */
  const goChat = () => {
    const t = input.trim();
    if (!t) return;
    navigate('/chat?trial=1', { state: { draft: t } });
    setInput('');
  };

  const quickActions=[
    {icon:<RobotOutlined/>,color:'#215fc6',bg:'#e8f0fe',label:'AI问诊',desc:'免登录试用',badge:'每日10次',ribbon:'免费',onClick:()=>navigate('/chat?trial=1')},
    {icon:<HeartOutlined/>,color:'#e53935',bg:'#fdecea',label:'中医体检',desc:'中医体质辨识 · 健康状态评估',badge:'即将上线'},
  ];
  const cardStyle=(delay)=>({background:'#fff',borderRadius:14,padding:'18px',border:`1px solid ${COLORS.border}`,boxShadow:'0 1px 4px rgba(0,0,0,.04)',animation:`xm-fadeUp .5s ease-out ${delay}s both`,transition:'all .25s ease',cursor:'default'});

  return (
    <div className="h5-root" style={{minHeight:'100vh',background:COLORS.pageBg,display:'flex',flexDirection:'column'}}>

      {/* ===== 导航栏 ===== */}
      <header style={{background:scrolled?'rgba(26,79,160,.95)':COLORS.gradient,backdropFilter:scrolled?'blur(12px)':'none',padding:'0 var(--r-pad)',height:56,display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100,boxShadow:scrolled?'0 2px 16px rgba(33,95,198,.3)':'0 2px 12px rgba(33,95,198,.25)',transition:'all .3s ease',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{width:32,height:32,borderRadius:8,background:'rgba(255,255,255,.2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden'}}>
            {siteCfg?.header_logo
              ? <img src={siteCfg.header_logo} alt="logo" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
              : <MedicineBoxOutlined style={{fontSize:18,color:'#fff'}}/>}
          </div>
          <div style={{lineHeight:1}}>
            <div style={{fontSize:15,fontWeight:700,color:'#fff',lineHeight:1.2}}>{siteCfg?.header_brand_name||'小铭健康'}</div>
            <div style={{fontSize:10,color:'rgba(255,255,255,.65)',letterSpacing:1,marginTop:2}}>{siteCfg?.header_brand_sub||'AI智能辅诊'}</div>
          </div>
        </div>
        <div className="nav-desktop" style={{display:'flex',gap:8,alignItems:'center'}}>
          {headerNavs.map(n=><Button key={n.label+n.link} size="small" ghost style={{borderRadius:6,fontSize:12,height:30,borderColor:'rgba(255,255,255,.5)',color:'#fff'}} onClick={()=>goLink(n.link)}>{n.label}</Button>)}
        </div>
        <div className="nav-mobile" style={{display:'none'}}>
          <div onClick={()=>setShowMenu(!showMenu)} style={{width:32,height:32,borderRadius:8,background:'rgba(255,255,255,.15)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4,cursor:'pointer'}}>
            <div style={{width:16,height:2,background:'#fff',borderRadius:1}}/><div style={{width:16,height:2,background:'#fff',borderRadius:1}}/><div style={{width:16,height:2,background:'#fff',borderRadius:1}}/>
          </div>
        </div>
      </header>

      {/* 移动端菜单 */}
      {showMenu&&(
        <div style={{position:'fixed',top:56,left:0,right:0,zIndex:99,background:'rgba(26,79,160,.97)',backdropFilter:'blur(12px)',padding:'12px 20px 16px',boxShadow:'0 4px 12px rgba(0,0,0,.15)'}}>
          {headerNavs.map(n=><div key={n.label+n.link} onClick={()=>{goLink(n.link);setShowMenu(false)}} style={{padding:'10px 0',color:'#fff',fontSize:14,borderBottom:'1px solid rgba(255,255,255,.1)',cursor:'pointer'}}>{n.label}</div>)}
        </div>
      )}

      {/* ===== Hero ===== */}
      <div className="h5-main">
      <div className="h5-hero" style={{background:'linear-gradient(135deg,#1a4fa0 0%,#215fc6 30%,#3b82e3 60%,#5a9ae6 100%)',backgroundSize:'200% 200%',animation:'xm-gradientShift 8s ease infinite',padding:'56px 0 70px',textAlign:'center',position:'relative',overflow:'hidden'}}>
        <FloatingCircles/>
        <div className="r-public-col" style={{position:'relative',zIndex:1,textAlign:'center'}}>
          <div style={{display:'inline-block',padding:'5px 18px',background:'rgba(255,255,255,.15)',borderRadius:20,color:'#fff',fontSize:13,marginBottom:20,border:'1px solid rgba(255,255,255,.2)',animation:'xm-fadeUp .6s ease-out both'}}>
            <ThunderboltOutlined style={{fontSize:13,marginRight:6}} />{siteCfg?.hero_tag||'基于大模型 · 中医智能诊疗'}
          </div>
          <div style={{maxWidth:'var(--r-hero-text)',margin:'0 auto'}}>
            <h1 className="hero-title" style={{color:'#fff',fontWeight:700,margin:'0 0 12px',lineHeight:1.4,animation:'xm-fadeUp .6s ease-out .15s both'}}>
              {siteCfg?.hero_title||'AI赋能中医诊疗'}<br/>
              <span className="hero-subtitle" style={{fontWeight:400,opacity:.9}}>{siteCfg?.hero_subtitle||'让每个诊所都有"老中医"'}</span>
            </h1>
            <p className="hero-desc" style={{color:'rgba(255,255,255,.8)',margin:0,lineHeight:1.7,animation:'xm-fadeUp .6s ease-out .3s both'}}>
              {siteCfg?.hero_desc||'融合舌诊、面诊、问诊AI技术，结合经典方剂库，为您提供专业的中医辅助诊断与处方建议'}
            </p>
          </div>
        </div>
      </div>

      {/* ===== 快捷功能卡片 ===== */}
      <div className="r-public-col" style={{marginTop:-38,position:'relative',zIndex:10}}>
        <div className="h5-func" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,background:'#fff',borderRadius:16,padding:'28px 32px',boxShadow:'0 4px 24px rgba(33,95,198,.12)',animation:'xm-fadeUp .5s ease-out .4s both'}}>
          {quickActions.map((a,i)=>(
            <div key={i} className="h5-func-item" onClick={a.onClick} style={{position:'relative',overflow:'hidden',display:'flex',flexDirection:'column',alignItems:'center',gap:10,cursor:'pointer',padding:'20px 10px',borderRadius:12,border:`1px solid ${COLORS.border}`,transition:'all .25s ease'}}
              onMouseEnter={e=>{e.currentTarget.style.background=a.bg;e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow='0 6px 16px rgba(0,0,0,.08)';e.currentTarget.style.borderColor=a.color+'55'}}
              onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='none';e.currentTarget.style.borderColor=COLORS.border}}
            >
              <div className="h5-func-icon" style={{width:54,height:54,borderRadius:14,background:a.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:26,color:a.color}}>{a.icon}</div>
              {a.ribbon&&(
                <div style={{position:'absolute',top:0,right:0,width:84,height:84,overflow:'hidden',borderTopRightRadius:12,pointerEvents:'none'}}>
                  <div style={{position:'absolute',top:-60,right:-60,width:120,height:120,transform:'rotate(45deg)',background:'linear-gradient(135deg,#ff5a52,#e53935)',boxShadow:'0 2px 8px rgba(229,57,53,.35)'}} />
                  <div style={{position:'absolute',top:25,right:-35,width:130,textAlign:'center',transform:'rotate(45deg)',color:'#fff',fontSize:11,fontWeight:700,letterSpacing:6,paddingLeft:6}}>{a.ribbon}</div>
                </div>
              )}
              <div style={{textAlign:'center'}}>
                <div style={{fontSize:15,fontWeight:600,color:COLORS.textMain}}>{a.label}</div>
                <div style={{fontSize:12,color:COLORS.textLight,marginTop:4}}>{a.desc}</div>
                <div style={{marginTop:8,display:'inline-block',fontSize:11,padding:'2px 10px',borderRadius:10,background:a.bg,color:a.color,fontWeight:600}}>{a.badge}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== 产品亮点 ===== */}
      <div className="r-public-col" style={{paddingTop:36,paddingBottom:24}}>
        <div className="h5-mobile" style={{textAlign:'center',fontSize:15,fontWeight:600,color:COLORS.textMain,marginBottom:16,display:'none'}}>✦ 核心优势 ✦</div>
        <div className="h5-hl">
          {[
            {icon:<SafetyCertificateOutlined/>,title:'安全可靠',desc:'医疗数据本地存储',color:'#215fc6'},
            {icon:<ExperimentOutlined/>,title:'四诊合参',desc:'多模态AI分析',color:'#e65100'},
            {icon:<FileTextOutlined/>,title:'智能开方',desc:'经典方剂匹配',color:'#2e7d32'},
            {icon:<TeamOutlined/>,title:'患者管理',desc:'完整档案记录',color:'#7b1fa2'},
          ].map((item,i)=>(
            <div key={i} style={cardStyle(.5+i*.1)}
              onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow='0 8px 20px rgba(33,95,198,.1)'}}
              onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,.04)'}}
            >
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:40,height:40,borderRadius:10,background:item.color+'12',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,color:item.color,flexShrink:0}}>{item.icon}</div>
                <div><div style={{fontSize:14,fontWeight:600,color:COLORS.textMain}}>{item.title}</div><div style={{fontSize:12,color:COLORS.textSub,marginTop:2}}>{item.desc}</div></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== 输入框 ===== */}
      <div className="r-public-col" style={{paddingTop:12,paddingBottom:28}}>
        <div style={{maxWidth:'var(--r-form)',margin:'0 auto',display:'flex',gap:10,alignItems:'center'}}>
          <div style={{flex:1,position:'relative'}}>
            <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();goChat()}}} placeholder="描述您的症状或健康问题..."
              style={{width:'100%',height:44,border:`1px solid ${COLORS.border}`,borderRadius:22,padding:'0 18px 0 40px',fontSize:14,outline:'none',color:COLORS.textMain,background:'#fff',transition:'all .25s ease',boxShadow:'0 1px 4px rgba(0,0,0,.04)'}}
              onFocus={e=>{e.target.style.borderColor=COLORS.primary;e.target.style.boxShadow='0 0 0 3px rgba(33,95,198,.1),0 2px 8px rgba(0,0,0,.06)'}}
              onBlur={e=>{e.target.style.borderColor=COLORS.border;e.target.style.boxShadow='0 1px 4px rgba(0,0,0,.04)'}}
            />
            <RobotOutlined style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',fontSize:16,color:COLORS.textLight}}/>
          </div>
          <button onClick={goChat} disabled={!input.trim()} style={{
            width:44,height:44,borderRadius:'50%',border:'none',
            background:input.trim()?COLORS.gradient:COLORS.border,
            color:'#fff',fontSize:17,cursor:input.trim()?'pointer':'not-allowed',
            display:'flex',alignItems:'center',justifyContent:'center',
            transition:'all .25s ease',flexShrink:0,
            boxShadow:input.trim()?'0 3px 10px rgba(33,95,198,.3)':'none',
          }}>
            <RobotOutlined style={{fontSize:18}}/>
          </button>
        </div>
        <div style={{maxWidth:'var(--r-form)',margin:'8px auto 0',textAlign:'center'}}><span style={{fontSize:11,color:COLORS.textLight}}>⚠️ AI辅诊结果仅供参考，不能替代专业医生诊断</span></div>
      </div>
      </div>

      {/* Footer */}
      <BrandFooter siteCfg={siteCfg}/>
    </div>
  );
};

export default PublicHomePage;
